import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { FUNDTAPAUURL } from "../../utils/constants/appConstants";
import FundtapWordmark from "./FundtapWordmark";
import AuthHelp from "./AuthHelp";
import NetworkStatus from "../../utils/networkStatus";
import { LOGIN, SIGN_UP } from "../../route/paths";

/**
 * Light-mode auth header — the marketing capsule (16px glass, 1200px container)
 * on the white auth page. Logo left; help + cross-links right.
 *
 * Motion: the bar is in-flow (not sticky), so it scrolls up and away with the
 * page. It does a light pull-down bounce when the scroll returns to the very top
 * (all sizes). Below 850px (the xs breakpoint) the cross-links collapse into a
 * hamburger → small dropdown, and "Need help?" becomes an icon.
 *
 * Context-aware: the button for the page you're on is hidden (per current route).
 */
const AuthHeader = () => {
  const { pathname } = useLocation();
  const onLogin = pathname.startsWith("/login");
  const onSignup = pathname.startsWith("/sign-up");

  const hdrRef = useRef(null);
  const navRef = useRef(null);
  const burgerRef = useRef(null);
  const [bounce, setBounce] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Light pull-down bounce when the scroll returns to the very top. The scroll
  // container is .auth-viewport (not the window), so listen there.
  useEffect(() => {
    const scroller = hdrRef.current && hdrRef.current.closest(".auth-viewport");
    if (!scroller) return undefined;
    let last = 0;
    let bounceTimer;
    const onScroll = () => {
      const top = scroller.scrollTop;
      if (top <= 0 && last > 2) {
        setBounce(true);
        clearTimeout(bounceTimer);
        bounceTimer = setTimeout(() => setBounce(false), 550);
      }
      last = top > 0 ? top : 0;
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      clearTimeout(bounceTimer);
    };
  }, []);

  // Close the dropdown on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      // The listener is on the document, so Escape reaches it whenever the
      // dropdown is open. Take focus back only when it is inside the dropdown
      // about to unmount, which would otherwise drop it on <body> and send the
      // next Tab to the top of the page; pulling focus in any other case would
      // yank the reader out of whatever they were doing.
      const focusWasInside =
        navRef.current && navRef.current.contains(document.activeElement);
      setMenuOpen(false);
      if (focusWasInside && burgerRef.current) burgerRef.current.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  // Close on navigation.
  useEffect(() => setMenuOpen(false), [pathname]);

  return (
    <header
      ref={hdrRef}
      className="auth-hdr"
      data-bounce={bounce ? "true" : "false"}
    >
      {/* Offline / back-online toasts on the auth pages (renders nothing). */}
      <NetworkStatus />
      <div className="auth-hdr-bar">
        <a
          className="auth-hdr-logo"
          href={FUNDTAPAUURL}
          aria-label="FundTap home"
        >
          <FundtapWordmark />
        </a>
        <div className="auth-hdr-actions">
          <AuthHelp />
          {!onLogin && (
            <Link
              className="auth-hbtn auth-hbtn--secondary auth-hdr-btn"
              to={LOGIN}
            >
              Log in
            </Link>
          )}
          {!onSignup && (
            <Link
              className="auth-hbtn auth-hbtn--secondary auth-hdr-btn"
              to={SIGN_UP}
            >
              Sign up
            </Link>
          )}

          {/* Mobile: hamburger → small dropdown */}
          <div
            className="auth-nav"
            ref={navRef}
            data-open={menuOpen ? "true" : "false"}
          >
            <button
              type="button"
              ref={burgerRef}
              className="auth-burger"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              data-open={menuOpen ? "true" : "false"}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <span />
              <span />
              <span />
            </button>
            {menuOpen && (
              <div className="auth-nav-menu" role="menu">
                {!onLogin && (
                  <Link className="auth-nav-item" to={LOGIN} role="menuitem">
                    Log in
                  </Link>
                )}
                {!onSignup && (
                  <Link className="auth-nav-item" to={SIGN_UP} role="menuitem">
                    Sign up
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default AuthHeader;
